import { Message, MessageType } from '../types';

export type MessageHandler = (message: Message) => void;

export type ReconnectCallback = () => void;

export class WebSocketService {
  private ws: WebSocket | null = null;
  private url: string;
  private messageHandlers: Set<MessageHandler> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private shouldReconnect = true;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private reconnectCallback: ReconnectCallback | null = null;
  private isReconnecting = false;

  constructor(url: string) {
    this.url = url;
  }

  setReconnectCallback(callback: ReconnectCallback | null) {
    this.reconnectCallback = callback;
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Close existing connection if any
        if (this.ws) {
          this.ws.close();
          this.ws = null;
        }

        // Clear any pending reconnect timeout
        if (this.reconnectTimeout) {
          clearTimeout(this.reconnectTimeout);
          this.reconnectTimeout = null;
        }

        this.ws = new WebSocket(this.url);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          const wasReconnecting = this.isReconnecting;
          this.reconnectAttempts = 0;
          this.shouldReconnect = true;
          this.isReconnecting = false;

          // Call reconnect callback if this was a reconnection
          if (wasReconnecting && this.reconnectCallback) {
            console.log('Calling reconnect callback');
            this.reconnectCallback();
          }

          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: Message = JSON.parse(event.data);
            this.messageHandlers.forEach(handler => handler(message));
          } catch (error) {
            console.error('Failed to parse message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          reject(error);
        };

        this.ws.onclose = () => {
          console.log('WebSocket disconnected');
          // Only attempt reconnect if it wasn't an intentional disconnect
          if (this.shouldReconnect) {
            this.attemptReconnect();
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  private attemptReconnect() {
    // Don't reconnect if we've exceeded max attempts or if reconnection is disabled
    if (!this.shouldReconnect || this.reconnectAttempts >= this.maxReconnectAttempts) {
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached');
      }
      return;
    }

    // Clear any existing timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectAttempts++;
    this.isReconnecting = true;
    console.log(`Reconnecting... Attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);

    this.reconnectTimeout = setTimeout(() => {
      this.connect().catch(err => {
        console.error('Reconnection failed:', err);
      });
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  send(message: Message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected');
    }
  }

  addMessageHandler(handler: MessageHandler) {
    this.messageHandlers.add(handler);
  }

  removeMessageHandler(handler: MessageHandler) {
    this.messageHandlers.delete(handler);
  }

  disconnect() {
    // Disable auto-reconnect before closing
    this.shouldReconnect = false;

    // Clear any pending reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Close the WebSocket
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  // Convenience methods for game actions
  joinGame(playerName: string, gameId?: string, playerId?: string) {
    this.send({
      type: MessageType.JOIN_GAME,
      payload: { playerName, gameId, playerId }
    });
  }

  addAIPlayer() {
    this.send({ type: MessageType.ADD_AI_PLAYER });
  }

  removeAIPlayer() {
    this.send({ type: MessageType.REMOVE_AI_PLAYER });
  }

  setDiceCount(dicePerPlayer: number) {
    this.send({
      type: MessageType.SET_DICE_COUNT,
      payload: { dicePerPlayer }
    });
  }

  startGame() {
    this.send({ type: MessageType.START_GAME });
  }

  makeBid(quantity: number, faceValue: number) {
    this.send({
      type: MessageType.MAKE_BID,
      payload: { quantity, faceValue }
    });
  }

  challenge() {
    this.send({ type: MessageType.CHALLENGE });
  }

  readyUp() {
    this.send({ type: MessageType.READY_UP });
  }

  leaveGame() {
    this.send({ type: MessageType.LEAVE_GAME });
  }
}
